import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, openBlock as _openBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = /*#__PURE__*/_createElementVNode("span", null, "La création d'un utilisateur nécessite la saisie des informations suivantes (un mot de passe sera généré et envoyé à l'utilisateur) :", -1 /* HOISTED */)
const _hoisted_2 = /*#__PURE__*/_createElementVNode("ul", { class: "q-mb-none" }, [
  /*#__PURE__*/_createElementVNode("li", { id: "username_example" }, " Un nom d'utilisateur (prénom ou prénom et initiale de nom) "),
  /*#__PURE__*/_createElementVNode("li", { id: "email_example" }, "Un email (exemple: toto@domaine.com)")
], -1 /* HOISTED */)
const _hoisted_3 = /*#__PURE__*/_createElementVNode("div", { class: "q-mx-sm q-mb-sm" }, "* : champs obligatoires", -1 /* HOISTED */)

import { useFocus } from '@vueuse/core';
import { ref, toRefs } from 'vue';
import { useQuasar, QInput } from 'quasar';

// PERSONAL COMPONENTS
import QBtnCancel from '@components/form/QBtnCancel.vue';
import QBtnValid from '@components/form/QBtnValid.vue';
import QDialogTemplate from '@components/template/QDialogTemplate.vue';

// ViewModel
import { useGlobalVM } from '@viewmodel/global.vm';

// PERSONAL LIBS
import adminRepository from '@data/repository/admin.repository';
import { checkEmail, checkUsername } from '@shared/lib/security.lib';
import { IApiResponse } from '@shared/interface/api-response.interface';
import { $t } from 'src/i18n/utils';
import {
  displayNotifSuccess,
  displayDialogError,
} from '@shared/lib/notify.lib';
import { emailRules, usernameRules } from '@shared/lib/form.lib';

/**
 * Events
 */

export default /*#__PURE__*/_defineComponent({
  __name: 'DialogUserCreate',
  props: {
  show: { type: Boolean, required: true, default: false },
},
  emits: ['close', 'success', 'failed'],
  setup(__props, { emit: __emit }) {

// VENDOR
const emits = __emit;

/**
 * Globals
 */
const $q = useQuasar();

/**
 * HTMLElement ref
 */
const qInputEmailRef = ref<QInput>();
const qInputUsernameRef = ref<QInput>();
const { focused: qInputEmailRefFocused } = useFocus(qInputEmailRef);
const { focused: qInputUsernameRefFocused } = useFocus(qInputUsernameRef);

/**
 * Viewmodel
 */
const { isMobile } = useGlobalVM();

/**
 * Reactive datas
 */
const loading = ref<boolean>(false);
const username = ref<string | undefined>(undefined);
const email = ref<string | undefined>(undefined);

/**
 * Properties
 */
const props = __props;
const { show } = toRefs(props);

/**
 * Methods
 */
function onClose() {
  emits('close');
}

function clearUsername() {
  qInputUsernameRef.value?.resetValidation();
  qInputUsernameRefFocused.value = true;
  username.value = undefined;
}

function clearEmail() {
  qInputEmailRef.value?.resetValidation();
  qInputEmailRefFocused.value = true;
  email.value = undefined;
}

function onValid() {
  qInputUsernameRef.value?.validate();
  qInputEmailRef.value?.validate();

  if (qInputUsernameRef.value?.hasError || qInputEmailRef.value?.hasError) {
    return;
  }

  loading.value = true;

  adminRepository
    .addUser(username.value, email.value)
    .then((rslt: IApiResponse<string, string> | undefined) => {
      if (rslt?.returnCode === 'ADMIN_ADD_USER_OK_1') {
        displayNotifSuccess(
          `Utilisateur '${username.value}' créé avec succès.`,
        );
      } else if (rslt?.returnCode === 'ADMIN_ADD_USER_OK_2') {
        displayDialogError({
          message: `Utilisateur '${username.value}' créé avec succès mais le mail contenant le mot de passe n\'a pas pu être envoyé'.`,
        });
      }

      emits('success');
      onClose();
    })
    .catch((error) => {
      displayDialogError({
        message: error.message ?? 'null',
      });
      emits('failed');
    })
    .finally(() => {
      loading.value = false;
      clearInputs();
      onClose();
    });
}

function clearInputs() {
  username.value = undefined;
  email.value = undefined;
}

return (_ctx: any,_cache: any) => {
  const _component_q_card_section = _resolveComponent("q-card-section")!
  const _component_q_icon = _resolveComponent("q-icon")!
  const _component_q_btn = _resolveComponent("q-btn")!
  const _component_q_separator = _resolveComponent("q-separator")!
  const _component_q_card_actions = _resolveComponent("q-card-actions")!

  return (_openBlock(), _createBlock(QDialogTemplate, {
    "show-dialog": _unref(show),
    title: "Création",
    "disable-close-button": loading.value,
    maximized: _unref(isMobile),
    persistent: loading.value,
    onClose: _cache[6] || (_cache[6] = ($event: any) => (onClose()))
  }, {
    content: _withCtx(() => [
      _createVNode(_component_q_card_section, null, {
        default: _withCtx(() => [
          _hoisted_1,
          _hoisted_2
        ]),
        _: 1 /* STABLE */
      }),
      _createCommentVNode(" Username "),
      _createVNode(_component_q_card_section, null, {
        default: _withCtx(() => [
          _hoisted_3,
          _createVNode(_unref(QInput), {
            ref_key: "qInputUsernameRef",
            ref: qInputUsernameRef,
            "aria-required": "true",
            "aria-describedby": "username_example",
            "aria-invalid": _unref(checkUsername)(username.value) ? 'false' : 'true',
            outlined: "",
            autocomplete: "username",
            modelValue: username.value,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((username).value = $event)),
            disable: loading.value,
            type: "text",
            label: _unref($t)('form.username.input.label'),
            "bg-color": _unref($q).dark.isActive ? 'black' : 'white',
            "label-color": _unref($q).dark.isActive ? 'white' : 'black',
            "input-class": [
            _unref($q).dark.isActive ? 'text-white' : 'text-black',
            'set-outline',
          ],
            "hide-bottom-space": "",
            "lazy-rules": "",
            rules: _unref(usernameRules),
            enterkeyhint: "done"
          }, {
            prepend: _withCtx(() => [
              _createVNode(_component_q_icon, { name: "person" })
            ]),
            append: _withCtx(() => [
              _createVNode(_component_q_btn, {
                flat: "",
                round: "",
                dense: "",
                icon: "cancel",
                class: "set-outline",
                title: _unref($t)('form.username.reset'),
                onClick: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (clearUsername()), ["stop"]))
              }, null, 8 /* PROPS */, ["title"])
            ]),
            _: 1 /* STABLE */
          }, 8 /* PROPS */, ["aria-invalid", "modelValue", "disable", "label", "bg-color", "label-color", "input-class", "rules"]),
          _createVNode(_component_q_separator, { class: "q-my-xs bg-transparent" }),
          _createCommentVNode(" Email "),
          _createVNode(_unref(QInput), {
            ref_key: "qInputEmailRef",
            ref: qInputEmailRef,
            "aria-required": "true",
            "aria-describedby": "email_example",
            "aria-invalid": _unref(checkEmail)(email.value) ? 'false' : 'true',
            outlined: "",
            modelValue: email.value,
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((email).value = $event)),
            disable: loading.value,
            type: "email",
            autocomplete: "email",
            label: _unref($t)('form.email.input.label'),
            "bg-color": _unref($q).dark.isActive ? 'black' : 'white',
            "label-color": _unref($q).dark.isActive ? 'white' : 'black',
            "input-class": [
            _unref($q).dark.isActive ? 'text-white' : 'text-black',
            'set-outline',
          ],
            "hide-bottom-space": "",
            "lazy-rules": "",
            rules: _unref(emailRules),
            enterkeyhint: "done"
          }, {
            prepend: _withCtx(() => [
              _createVNode(_component_q_icon, { name: "mail" })
            ]),
            append: _withCtx(() => [
              _createVNode(_component_q_btn, {
                flat: "",
                round: "",
                dense: "",
                icon: "cancel",
                class: "set-outline",
                title: _unref($t)('form.email.reset'),
                onClick: _cache[2] || (_cache[2] = _withModifiers(($event: any) => (clearEmail()), ["stop"]))
              }, null, 8 /* PROPS */, ["title"])
            ]),
            _: 1 /* STABLE */
          }, 8 /* PROPS */, ["aria-invalid", "modelValue", "disable", "label", "bg-color", "label-color", "input-class", "rules"])
        ]),
        _: 1 /* STABLE */
      })
    ]),
    footer: _withCtx(() => [
      _createVNode(_component_q_separator),
      _createVNode(_component_q_card_actions, { align: "right" }, {
        default: _withCtx(() => [
          _createVNode(QBtnCancel, {
            label: 'Annuler',
            onClick: _cache[4] || (_cache[4] = ($event: any) => (onClose())),
            disable: loading.value
          }, null, 8 /* PROPS */, ["disable"]),
          _createVNode(QBtnValid, {
            label: 'Créer l\'utilisateur',
            onClick: _cache[5] || (_cache[5] = ($event: any) => (onValid())),
            disable: loading.value,
            loading: loading.value,
            "loading-label": ''
          }, null, 8 /* PROPS */, ["disable", "loading"])
        ]),
        _: 1 /* STABLE */
      })
    ]),
    _: 1 /* STABLE */
  }, 8 /* PROPS */, ["show-dialog", "disable-close-button", "maximized", "persistent"]))
}
}

})